import React, { useCallback, useEffect, useState } from 'react';
import { MapPin, Building2 } from 'lucide-react';
import AddressAutocomplete from './AddressAutocomplete';
import { validateSharedTransferLocation } from '../services/sharedTransferLocationApi';
import {
  getDefaultPoint,
  HUB_BIAS_BY_CITY,
} from '../config/sharedTransferLocations';
import { MAIN_STATION_BY_CITY } from '../config/busScheduleMeta';
import { supabase } from '../config/supabase';

/**
 * Pickup/dropoff within a locked hub city — defaults to Riverside main station.
 */
const SharedTransferPointPicker = ({
  hubCity,
  kind = 'pickup',
  value,
  onChange,
  disabled = false,
  showOrderNotes = false,
  orderNotes = '',
  onOrderNotesChange,
}) => {
  const city = String(hubCity || '');
  const bias = HUB_BIAS_BY_CITY[city];
  const stationName = MAIN_STATION_BY_CITY[city];
  const [mode, setMode] = useState(value?.type === 'custom' ? 'custom' : 'main');
  const [customValue, setCustomValue] = useState(
    value?.type === 'custom' ? value : null,
  );
  const [error, setError] = useState('');
  const [validating, setValidating] = useState(false);
  const [customDraftKey, setCustomDraftKey] = useState(0);
  const [isEditingCustom, setIsEditingCustom] = useState(false);

  useEffect(() => {
    if (!city) return;
    if (value?.type === 'custom') {
      setMode('custom');
      setCustomValue(value);
      setIsEditingCustom(!value.placeId);
    } else if (value?.type === 'main_station') {
      setMode('main');
      setCustomValue(null);
      setIsEditingCustom(false);
    }
    // When value is null, keep the current mode — user may be picking a custom address.
    setError('');
  }, [city, value]);

  const applyMainStation = useCallback(() => {
    const def = getDefaultPoint(city);
    onChange(def);
    setError('');
  }, [city, onChange]);

  useEffect(() => {
    if (!city || disabled) return;
    if (mode === 'main' && value?.type !== 'main_station') {
      applyMainStation();
    }
  }, [city, mode, disabled, value?.type, applyMainStation]);

  const handleModeChange = (next) => {
    setError('');
    setMode(next);
    if (next === 'main') {
      applyMainStation();
    } else {
      setCustomValue(null);
      setIsEditingCustom(true);
      setCustomDraftKey((k) => k + 1);
      onChange(null);
    }
  };

  const startCustomEdit = () => {
    setError('');
    setIsEditingCustom(true);
    setCustomDraftKey((k) => k + 1);
    setCustomValue(null);
    onChange(null);
  };

  const handleCustomChange = async (place) => {
    if (!place?.placeId) {
      setCustomValue(null);
      onChange(null);
      setError('');
      return;
    }

    setValidating(true);
    setError('');
    try {
      const session = (await supabase.auth.getSession()).data.session;
      const result = await validateSharedTransferLocation(
        {
          hubCity: city,
          point: {
            type: 'custom',
            label: place.label,
            placeId: place.placeId,
            lat: place.lat,
            lng: place.lng,
          },
        },
        session?.access_token,
      );

      const validated = {
        type: 'custom',
        label: result.point?.label || place.label,
        placeId: result.point?.placeId || place.placeId,
        lat: result.point?.lat ?? place.lat,
        lng: result.point?.lng ?? place.lng,
      };
      setCustomValue(validated);
      onChange(validated);
      setIsEditingCustom(false);
    } catch (err) {
      setCustomValue(null);
      onChange(null);
      setError(err.message || 'Address is outside the selected city service area.');
    } finally {
      setValidating(false);
    }
  };

  if (!city) {
    return (
      <p className="text-xs text-stone-500">Select your route segment first.</p>
    );
  }

  const labelPrefix = kind === 'dropoff' ? 'Drop-off' : 'Pickup';

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm font-semibold text-[#2D1E17]">
        <MapPin size={14} className="text-[#A44432]" />
        {labelPrefix} in {city}
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={disabled}
          onClick={() => handleModeChange('main')}
          className={`text-xs px-3 py-2 rounded-xl border transition-colors ${
            mode === 'main'
              ? 'border-[#A44432] bg-[#FAF3E0] text-[#2D1E17]'
              : 'border-[#E8DED1] text-[#7A6E67] hover:border-stone-300'
          }`}
        >
          <span className="inline-flex items-center gap-1">
            <Building2 size={12} />
            Riverside main station ({stationName || city})
          </span>
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => handleModeChange('custom')}
          className={`text-xs px-3 py-2 rounded-xl border transition-colors ${
            mode === 'custom'
              ? 'border-[#A44432] bg-[#FAF3E0] text-[#2D1E17]'
              : 'border-[#E8DED1] text-[#7A6E67] hover:border-stone-300'
          }`}
        >
          Custom address
        </button>
      </div>

      {mode === 'main' && value?.label && (
        <p className="text-sm text-[#2D1E17] bg-stone-50 border border-[#E8DED1] rounded-xl px-3 py-2">
          {value.label}
        </p>
      )}

      {mode === 'custom' && (
        <div className="space-y-1">
          {customValue?.placeId && !isEditingCustom ? (
            <div className="space-y-2">
              <p className="text-sm text-[#2D1E17] bg-stone-50 border border-[#E8DED1] rounded-xl px-3 py-2">
                {customValue.label}
              </p>
              <button
                type="button"
                disabled={disabled || validating}
                onClick={startCustomEdit}
                className="text-xs font-semibold text-[#A44432] underline hover:no-underline"
              >
                Change address
              </button>
            </div>
          ) : (
            <AddressAutocomplete
              key={`${kind}-${city}-${customDraftKey}`}
              id={`${kind}-${city}-address`}
              label={`Search address in ${city}`}
              value={null}
              onChange={handleCustomChange}
              placeholder={`Search within ${city}`}
              biasCenter={bias ? { lat: bias.lat, lng: bias.lng } : undefined}
              biasRadiusKm={bias?.radiusKm}
              disabled={disabled || validating}
            />
          )}
          {validating && (
            <p className="text-xs text-stone-500">Checking address…</p>
          )}
        </div>
      )}

      {error && (
        <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
          {error}
        </p>
      )}

      {showOrderNotes && onOrderNotesChange && (
        <div className="space-y-1.5">
          <label className="block text-xs font-medium text-[#7A6E67]">
            Order notes (flight number, gate code, etc.)
          </label>
          <textarea
            className="input-field min-h-[70px] resize-none"
            rows={2}
            value={orderNotes}
            onChange={(e) => onOrderNotesChange(e.target.value)}
            disabled={disabled}
            placeholder="Optional — e.g. flight number, hotel room, landmark"
          />
        </div>
      )}
    </div>
  );
};

export default SharedTransferPointPicker;
